import { Alert, Box, Stack, Typography } from '@mui/material';
import { useState } from 'react';

import { useUndoRedoKeyboardShortcuts } from '@/components/diagrams/FlowChart/hooks/useUndoRedoKeyboardShortcuts';
import { Button } from '@/components/form/baseInputs/Button';
import { UnsavedChangesConfirmationModal } from '@/components/modals/ConfirmationModal/UnsavedChangesConfirmationModal';
import { ModalHeader } from '@/components/modals/FullScreenModal/FullScreenFormLayout';
import { FullScreenModal } from '@/components/modals/FullScreenModal/FullScreenModal';
import { useOnBeforeUnloadPrompt } from '@/hooks/useOnBeforeUnloadPrompt';
import { FullScreenModalHouseholdChatHost } from '@/modules/household/chat/components/FullScreenModalHouseholdChatHost';
import { TrowserKey } from '@/modules/navigation/trowsers/trowser.types';

import { type NewDiagramPrompt } from '../../contexts/artifactStudio.context';
import { useDiagramEditorContext } from '../../contexts/diagramEditor.context';
import {
  type DiagramEditorLock,
  useDiagramEditorLock,
} from '../../hooks/useDiagramEditorLock';
import { useNewDiagramChat } from '../../hooks/useNewDiagramChat';
import { DiagramEditorCanvas } from './DiagramEditorCanvas';
import { DiagramEditorTitle } from './DiagramEditorTitle';

const LOCK_STATUS: Record<DiagramEditorLock, string> = {
  saving: 'Saving — edits paused',
  generating: 'Generating — edits paused',
};

interface DiagramEditorModalProps {
  newDiagram: NewDiagramPrompt | undefined;
  onArtifactCreated: (artifactID: string) => void;
}

export function DiagramEditorModal({
  newDiagram,
  onArtifactCreated,
}: DiagramEditorModalProps) {
  const editor = useDiagramEditorContext();
  const { artifactID, isDirty } = editor;
  const lock = useDiagramEditorLock();
  const [isCloseConfirmationOpen, setIsCloseConfirmationOpen] = useState(false);
  useNewDiagramChat({ artifactID, newDiagram, onArtifactCreated });
  useOnBeforeUnloadPrompt(isDirty);
  useUndoRedoKeyboardShortcuts({
    ...editor.history,
    canUndo: editor.history.canUndo && !lock,
    canRedo: editor.history.canRedo && !lock,
  });

  const handleCancel = () => {
    if (isDirty) {
      setIsCloseConfirmationOpen(true);
      return;
    }
    editor.close();
  };
  const done = async () => {
    if (isDirty && !(await editor.save())) return;
    editor.close();
  };

  // The modal gets no `onClose`, so Escape never closes the editor. Escape
  // belongs to the open menu, popover, inline editor, or placement tool.
  return (
    <FullScreenModal
      trowserKey={TrowserKey.Artifact}
      isOpen
      disableEnforceFocus
    >
      <Stack height="100%" minHeight={0}>
        <Box position="relative">
          <ModalHeader
            heading={artifactID ? <DiagramEditorTitle /> : 'Create diagram'}
            rightContent={
              <Stack direction="row" spacing={1}>
                <Button variant="secondary" size="sm" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  disabled={lock !== null}
                  onClick={() => {
                    void done();
                  }}
                >
                  Done
                </Button>
              </Stack>
            }
          />
          {lock && (
            <Typography
              variant="body-xs"
              role="status"
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none',
              }}
            >
              {LOCK_STATUS[lock]}
            </Typography>
          )}
        </Box>
        <Box flex={1} minHeight={0}>
          <FullScreenModalHouseholdChatHost
            viewedContext={null}
            entryPointBottomOffset={16}
          >
            {({ householdChatPane }) => (
              <Box display="flex" height="100%" minHeight={0}>
                <Stack flex={1} minWidth={0} minHeight={0} position="relative">
                  {editor.error && (
                    <Alert severity="error">{editor.error}</Alert>
                  )}
                  <DiagramEditorCanvas />
                </Stack>
                {householdChatPane}
              </Box>
            )}
          </FullScreenModalHouseholdChatHost>
        </Box>
      </Stack>
      {isCloseConfirmationOpen && (
        <UnsavedChangesConfirmationModal
          isOpen
          onClose={(reason) => {
            setIsCloseConfirmationOpen(false);
            // The shared dialog reports Leave as 'cancel' and Stay as 'confirm'.
            if (reason === 'cancel') editor.close();
          }}
        />
      )}
    </FullScreenModal>
  );
}
